/**
 * 去除 URL search 中的 hash，避免锚点变化触发新的 Snowplow page view。
 */
function removeHashFragment(search: string): string {
  return search.split('#')[0] ?? '';
}

/**
 * Snowplow page view 去重时需要忽略的 URL 清理参数。
 */
const SNOWPLOW_PAGE_VIEW_ROUTE_KEY_IGNORED_PARAMS = ['showLogin', 'from', 'reason'] as const;

/**
 * 归一化 Snowplow page view 去重用 search，避免 replaceState 清理内部参数时重复上报。
 */
function normalizeSnowplowPageViewSearch(search: string): string {
  const cleanSearch = removeHashFragment(search);
  if (!cleanSearch) {
    return '';
  }

  const queryText = cleanSearch.startsWith('?') ? cleanSearch.slice(1) : cleanSearch;
  if (!queryText) {
    return '';
  }

  const params = new URLSearchParams(queryText);
  const hasIgnoredParam = SNOWPLOW_PAGE_VIEW_ROUTE_KEY_IGNORED_PARAMS.some((paramName) =>
    params.has(paramName),
  );

  if (!hasIgnoredParam) {
    return cleanSearch;
  }

  SNOWPLOW_PAGE_VIEW_ROUTE_KEY_IGNORED_PARAMS.forEach((paramName) => {
    params.delete(paramName);
  });

  return params.toString();
}

/**
 * 构建 Snowplow page view 去重用路由 key；返回 null 表示路由尚未就绪。
 */
export function buildSnowplowPageViewRouteKey(
  pathname: string | null,
  search: string | null | undefined,
): string | null {
  if (!pathname) {
    return null;
  }

  const cleanSearch = normalizeSnowplowPageViewSearch(search ?? '');
  if (!cleanSearch) {
    return pathname;
  }

  return `${pathname}${cleanSearch.startsWith('?') ? cleanSearch : `?${cleanSearch}`}`;
}

interface SnowplowPageViewLocationSnapshot {
  pathname: string;
  search: string;
}

/**
 * 基于同一个 location 快照构建路由 key，避免旧 pathname 与新 search 混合导致重复上报。
 */
export function buildSnowplowPageViewRouteKeyFromLocation(
  fallbackPathname: string | null,
  locationSnapshot: SnowplowPageViewLocationSnapshot | null,
): string | null {
  return buildSnowplowPageViewRouteKey(
    locationSnapshot?.pathname || fallbackPathname,
    locationSnapshot?.search,
  );
}
